import React from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import PlayerPanel from "core/components/PlayerPanel";
import useRegisterOccupiedZone from "core/hooks/useRegisterOccupiedZone";
import FullscreenButton from "core/components/FullscreenButton";
import QualityButton from "core/components/QualityButton";
import MuteButton from "core/components/MuteButton";
import ShareButton from "core/components/ShareButton";
import LogoButton from "core/components/LogoButton";
import RelatedButton from "core/components/RelatedButton";
import Duration from "core/components/Duration";
import LiveButton from "core/components/LiveButton";
import LiveSeekBar from "core/components/LiveSeekBar";
import VodSeekBar from "core/components/VodSeekBar";
import PauseButton from "core/components/PauseButton";
import PlayButton from "core/components/PlayButton";
import PipButton from "core/components/PipButton";
import RewindButton from "core/components/RewindButton";
import CurrentTime from "core/components/CurrentTime";
import { useMediaQuery } from "@mui/material";
import contentUrl from "core/utils/contentUrl";
import CastButton from "core/components/CastButton";
import SubtitlesButton from "core/components/SubtitlesButton";
import AudioTrackButton from "core/components/AudioTrackButton";
import EpgButton from "core/components/EpgButton/EpgButton";
import { isTelegramMiniApp } from "core/utils/telegram";
import LabelsToggleButton from "core/components/LabelsToggleButton";
import PlaybackSpeedButton from "core/components/PlaybackSpeedButton";
import { useArena } from "core/components/ArenaProvider";

const Root = styled("div")(({ theme }) => ({
  display: "flex",
  width: "auto",
  position: "absolute",
  minHeight: 56,
  alignItems: "center",
  left: theme.spacing(1),
  right: theme.spacing(1),
  "@media (max-width: 450px)": {
    left: theme.spacing(0.5),
    right: theme.spacing(0.5),
  },
  [theme.breakpoints.up("sm")]: {
    left: theme.spacing(2),
    right: theme.spacing(2),
  },
  [theme.breakpoints.up("md")]: {
    left: theme.spacing(3),
    right: theme.spacing(3),
  },
}));

const LeftControls = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  [theme.breakpoints.up("sm")]: {
    gap: theme.spacing(0.75),
  },
}));

const RightControls = styled("div")(({ theme }) => ({
  marginLeft: "auto",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  [theme.breakpoints.up("sm")]: {
    gap: theme.spacing(0.75),
  },
}));

function PlayerFooter({
  shareOnMobile,
  onClickRelated,
  start,
  finish,
  fullScreen,
  visible,
  position,
  videoElement,
  liveSeekBar,
  videoState,
  videoActions,
  content,
  onClickPlay,
  onClickPause,
  onSeekStart,
  onSeek,
  onSeekEnd,
  hideShare,
  hideLogo,
  fullscreenTarget,
  videoPreviews,
  chooseCameraButton,
  disableSeekBarPopup,
  hlsUrl,
  isKeyboardSeeking,
  keyboardSeekValue,
}) {
  const isMobile = useMediaQuery("(max-width: 450px)");
  const arena = useArena();

  // В новом виде эмбеда «Поделиться» нужна и на телефоне: место под неё
  // освобождаем, убирая с телефона кнопку скорости воспроизведения.
  const showShare =
    isMobile && !shareOnMobile
      ? false
      : hideShare
      ? false
      : !content?.hideShare;
  const showSpeed = !(isMobile && shareOnMobile);
  const showQuality = isMobile ? false : true;
  const showRewind = videoState?.live && !isMobile;
  const showLogo = hideLogo ? false : !content?.hideLogo;

  const onClickRewind = () => {
    liveSeekBar.setOffset(liveSeekBar.offset + 10);
  };

  // Регистрируем footer зону постоянно, чтобы отступы не дергались при скрытии контролов
  const footerRef = useRegisterOccupiedZone("footer", true);

  return (
    <>
      <PlayerPanel visible={visible} position={position} disableGutters>
        <Root ref={footerRef}>
          <LeftControls>
            <PlayButton videoState={videoState} onClick={onClickPlay} />
            <PauseButton videoState={videoState} onClick={onClickPause} />

            {showRewind && (
              <RewindButton videoState={videoState} onClick={onClickRewind} />
            )}

            {chooseCameraButton}

            <LiveButton
              videoState={videoState}
              videoActions={videoActions}
              liveSeekBar={liveSeekBar}
            />

            {videoState?.live && content?.id && (
              <EpgButton contentId={content.id} />
            )}

            <CurrentTime liveSeekBar={liveSeekBar} />
            <Duration videoState={videoState} />
          </LeftControls>

          <RightControls>
            {showLogo && <LogoButton url={content.shareUrl} arena={arena} />}

            <MuteButton
              videoState={videoState}
              videoActions={videoActions}
              content={content}
            />

            {!isTelegramMiniApp && (
              <SubtitlesButton
                videoState={videoState}
                videoActions={videoActions}
              />
            )}

            <AudioTrackButton
              videoState={videoState}
              videoActions={videoActions}
            />

            {showQuality && (
              <QualityButton
                videoState={videoState}
                videoActions={videoActions}
              />
            )}
            {!videoState?.live && showSpeed && (
              <PlaybackSpeedButton
                videoState={videoState}
                videoActions={videoActions}
              />
            )}
            {onClickRelated && <RelatedButton onClick={onClickRelated} />}
            {showShare && !isTelegramMiniApp && (
              <ShareButton
                contentId={content.id}
                shareUrl={contentUrl(content.shareUrl || content.url, {
                  start,
                  finish,
                })}
                shareText={content.title}
                shareDescription={content.rightholder.name}
                shareImage={content.preview}
                getCurrentTime={
                  videoState?.live
                    ? undefined
                    : () => videoElement?.ref?.current?.currentTime
                }
              />
            )}
            {!isTelegramMiniApp && <PipButton videoElement={videoElement} />}
            {content && (
              <CastButton
                liveSeekBar={liveSeekBar}
                content={content}
                hlsUrl={hlsUrl}
              />
            )}
            {content?.contentTimecodes?.timecodes &&
              content.contentTimecodes.timecodes.length > 0 &&
              visible && <LabelsToggleButton />}

            {fullScreen && (
              <FullscreenButton
                videoElement={videoElement}
                target={fullscreenTarget}
                content={content}
              />
            )}
          </RightControls>
          <VodSeekBar
            videoState={videoState}
            videoActions={videoActions}
            videoPreviews={videoPreviews}
            visible={visible}
            onSeekStart={onSeekStart}
            onSeek={onSeek}
            onSeekEnd={onSeekEnd}
            disableSeekBarPopup={disableSeekBarPopup}
            timecodes={content?.contentTimecodes?.timecodes}
            isKeyboardSeeking={isKeyboardSeeking}
            keyboardSeekValue={keyboardSeekValue}
          />

          <LiveSeekBar
            liveSeekBar={liveSeekBar}
            videoState={videoState}
            videoActions={videoActions}
            visible={visible}
          />
        </Root>
      </PlayerPanel>
    </>
  );
}

PlayerFooter.propTypes = {
  shareOnMobile: PropTypes.bool,
  onClickRelated: PropTypes.func,
  fullscreenTarget: PropTypes.string,
  hideShare: PropTypes.bool,
  hideLogo: PropTypes.bool,
  fullScreen: PropTypes.bool,
  visible: PropTypes.bool,
  position: PropTypes.string,
  liveSeekBar: PropTypes.object,
  videoPreviews: PropTypes.object,
  hlsUrl: PropTypes.string,
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    logo: PropTypes.string,
    shareUrl: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    preview: PropTypes.string,
    hideShare: PropTypes.bool,
    hideLogo: PropTypes.bool,
    rightholder: PropTypes.shape({
      slug: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      hideLogo: PropTypes.bool, // unused
      hideShare: PropTypes.bool, // unused
    }),
    contentTimecodes: PropTypes.shape({
      id: PropTypes.string,
      timecodes: PropTypes.array,
    }),
  }),
  videoElement: PropTypes.node,
  videoState: PropTypes.object,
  videoActions: PropTypes.object,
  onClickPlay: PropTypes.func,
  onClickPause: PropTypes.func,
  onSeekStart: PropTypes.func,
  onSeek: PropTypes.func,
  onSeekEnd: PropTypes.func,
  disableSeekBarPopup: PropTypes.bool,
  chooseCameraButton: PropTypes.node,
  hideRelatedContent: PropTypes.bool,
  start: PropTypes.number,
  finish: PropTypes.number,
  isKeyboardSeeking: PropTypes.bool,
  keyboardSeekValue: PropTypes.number,
};

PlayerFooter.defaultProps = {
  fullScreen: true,
};

export default PlayerFooter;
